import React, { useState } from 'react';
import { Button, message } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { OutlookIcon } from '../../../design-system';
import axios from 'axios';
import { userService } from '../../../services/userService';
import { popupOAuth, oauthEvents } from './popupOAuth';

// API endpoint
const API_BASE_URL = 'https://5z9vmw0tg6.execute-api.eu-north-1.amazonaws.com/production';

interface OutlookOAuthButtonProps {
  onSuccess?: () => void;
  style?: React.CSSProperties;
  ownershipType?: 'personal' | 'shared';
}

interface OutlookAuthResponse {
  auth_url: string;
  ownership_type: string;
  expires_in: number;
}

const OutlookOAuthButton: React.FC<OutlookOAuthButtonProps> = ({ onSuccess, style, ownershipType = 'personal' }) => {
  const [loading, setLoading] = useState(false);

  // Using centralized userService.getAuthToken() instead of duplicate code

  const handleOutlookOAuth = async () => {
    setLoading(true);
    
    try {
      // Get authentication credentials
      const token = await userService.getAuthToken();
      
      console.log('[OutlookOAuth] Starting popup OAuth flow...');
      
      // Record when this OAuth session started
      const sessionStartTime = new Date().toISOString();
      
      // Get the OAuth URL from the backend (channel service endpoint)
      const response = await axios.post<OutlookAuthResponse>(
        `${API_BASE_URL}/channels/outlook/auth`,
        {
          ownership_type: ownershipType // Use the prop value
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('[OutlookOAuth] Auth URL received:', response.data.auth_url);
      
      // Create a function that gets fresh headers for each poll
      const getFreshHeaders = async () => {
        try {
          const freshToken = await userService.getAuthToken();
          return {
            'Authorization': `Bearer ${freshToken}`,
            'Content-Type': 'application/json'
          };
        } catch (error) {
          console.error('[OutlookOAuth] Error getting fresh headers:', error);
          return {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          };
        }
      };
      
      // Use popup OAuth instead of redirect with session_start parameter
      const result = await popupOAuth(
        response.data.auth_url,
        `${API_BASE_URL}/channels/outlook/status?session_start=${encodeURIComponent(sessionStartTime)}`, // Include session start time
        getFreshHeaders, // Pass function instead of static headers
        {
          popupTitle: 'Connect Outlook',
          width: 600,
          height: 700,
          pollInterval: 3000, // Check every 3 seconds
          maxAttempts: 30 // 90 seconds total
        }
      );
      
      if (result.success) {
        // message.success('Outlook connected successfully!');
        console.log('✅ Outlook channel connected:', result.data);
        
        // Emit global event for other components
        oauthEvents.emit('channelConnected', {
          type: 'outlook',
          data: result.data
        });
        
        if (onSuccess) {
          onSuccess();
        }
      } else {
        throw new Error(result.error || 'Authentication failed');
      }
      
    } catch (error: any) {
      console.error('[OutlookOAuth] Error in popup OAuth:', error);

      
      // Extract error message
      const errorMessage = error?.response?.data?.error || error?.message || 'Unknown error occurred';
      
      // message.error(`Failed to connect Outlook: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        icon={loading ? <LoadingOutlined /> : <OutlookIcon size={16} />}
        onClick={handleOutlookOAuth}
        loading={loading}
        style={{
          border: '1px solid #e5e7eb',
          backgroundColor: '#fff',
          color: '#374151',
          borderRadius: '6px',
          height: '36px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontWeight: 500,
          transition: 'all 0.2s ease',
          ...style
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = '#0078d4';
          e.currentTarget.style.color = '#0078d4';
          e.currentTarget.style.backgroundColor = '#f8fafc';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = '#e5e7eb';
          e.currentTarget.style.color = '#374151';
          e.currentTarget.style.backgroundColor = '#fff';
        }}
      >
        {loading ? 'Connecting...' : 'Outlook'}
      </Button>


    </>
  );
};

export default OutlookOAuthButton; 